import React, { useState, useEffect, useRef } from 'react';

/**
 * Feature Flags System
 * Enables feature toggling without code changes
 */

// Default feature flags configuration
const DEFAULT_FLAGS = {
  // UI Features
  newAssessmentUI: true,
  enhancedProfileView: true,
  skeletonLoaders: true,
  offlineMode: true,
  
  // Feature Modules
  clinicalAssessments: true,
  crmCompliance: true,
  privacyManagement: true,
  rosterCredentials: true,
  
  // Integrations
  medicareIntegration: false,
  mhrIntegration: false,
  escriptIntegration: false,
  pbsIntegration: false,
  acerIntegration: false,
  
  // Experimental
  aiAssistance: false,
  predictiveAnalytics: false,
  voiceInput: false,
  
  // Performance
  codeSplitting: true,
  lazyLoading: true,
  virtualization: true,
};

// Storage key for feature flags
const STORAGE_KEY = 'feature_flags';

/**
 * Get current feature flags
 * @returns {Object}
 */
export const getFeatureFlags = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? { ...DEFAULT_FLAGS, ...JSON.parse(stored) } : DEFAULT_FLAGS;
  } catch (error) {
    console.error('[FeatureFlags] Failed to load flags:', error);
    return DEFAULT_FLAGS;
  }
};

/**
 * Set a specific feature flag
 * @param {string} flag 
 * @param {boolean} value 
 */
export const setFeatureFlag = (flag, value) => {
  try {
    const current = getFeatureFlags();
    const updated = { ...current, [flag]: value };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    console.log(`[FeatureFlags] ${flag} set to ${value}`);
    return updated;
  } catch (error) {
    console.error('[FeatureFlags] Failed to set flag:', error);
    return getFeatureFlags();
  }
};

/**
 * Check if a feature is enabled
 * @param {string} flag 
 * @returns {boolean}
 */
export const isFeatureEnabled = (flag) => {
  const flags = getFeatureFlags();
  return flags[flag] || false;
};

/**
 * Reset feature flags to defaults
 */
export const resetFeatureFlags = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
    console.log('[FeatureFlags] Reset to defaults');
    return DEFAULT_FLAGS;
  } catch (error) {
    console.error('[FeatureFlags] Failed to reset flags:', error);
    return DEFAULT_FLAGS;
  }
};

/**
 * FeatureFlagsProvider Component
 * Provides feature flags context to the app
 */
export const FeatureFlagsProvider = ({ children }) => {
  const [flags, setFlags] = useState(getFeatureFlags());
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    // Load flags on mount
    setFlags(getFeatureFlags());
    setInitialized(true);
  }, []);

  const updateFlag = (flag, value) => {
    const updated = setFeatureFlag(flag, value);
    setFlags(updated);
  };

  const isEnabled = (flag) => flags[flag] || false;

  if (!initialized) {
    return null;
  }

  return (
    <FeatureFlagsContext.Provider value={{ flags, updateFlag, isEnabled }}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};

/**
 * useFeatureFlags Hook
 * Provides access to feature flags
 */
export const useFeatureFlags = () => {
  const context = React.useContext(FeatureFlagsContext);
  
  if (!context) {
    console.warn('[useFeatureFlags] Used outside FeatureFlagsProvider');
    return {
      flags: getFeatureFlags(),
      updateFlag: setFeatureFlag,
      isEnabled: isFeatureEnabled,
    };
  }

  return context;
};

/**
 * FeatureFlag Component
 * Conditionally renders children based on feature flag
 */
export const FeatureFlag = ({ flag, children, fallback = null }) => {
  const isEnabled = useFeatureFlags()?.isEnabled(flag) ?? isFeatureEnabled(flag);
  
  return isEnabled ? children : fallback;
};

// Create context for feature flags
const FeatureFlagsContext = React.createContext(null);

export default {
  getFeatureFlags,
  setFeatureFlag,
  isFeatureEnabled,
  resetFeatureFlags,
  FeatureFlagsProvider,
  useFeatureFlags,
  FeatureFlag,
  DEFAULT_FLAGS,
};
